import { useGLTF } from "@react-three/drei"
import { Suspense, useRef, useEffect, useState, Component, useCallback } from "react"
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { useLoadingTracker } from '../contexts/LoadingTrackerContext'
import { modelCache } from '../Utils/modelCache'
import { getModelArrayBuffer } from '../Utils/offlineCache'
import { tagObjectWithLayerKey } from '../Utils/pickLayerKey'
import { applyLayerAppearance } from '../Utils/layerAppearance'

// Configure drei's useGLTF to use DRACOLoader globally
// drei's useGLTF has a setDecoderPath method for this
if (typeof useGLTF.setDecoderPath === 'function') {
  useGLTF.setDecoderPath(`${process.env.PUBLIC_URL || ''}/draco/gltf/`);
}

// Shared MeshoptDecoder instance to avoid multiple WebAssembly allocations
// MeshoptDecoder needs to be prepared asynchronously, so we initialize it once
let sharedMeshoptDecoder = null;
let meshoptDecoderInitializing = false;

// Shared DRACOLoader instance to avoid multiple WebAssembly allocations
// Creating a new DRACOLoader for each model causes "Out of memory" errors
// when loading many models simultaneously
let sharedDracoLoader = null;

// Initialize MeshoptDecoder early to avoid memory issues
// This prepares the WebAssembly module once for all loaders
const initializeMeshoptDecoder = async () => {
  if (sharedMeshoptDecoder || meshoptDecoderInitializing) {
    return sharedMeshoptDecoder;
  }
  
  meshoptDecoderInitializing = true;
  try {
    await MeshoptDecoder.ready;
    sharedMeshoptDecoder = MeshoptDecoder;
    return sharedMeshoptDecoder;
  } catch (error) {
    console.warn('[LoadModel] Failed to initialize MeshoptDecoder:', error);
    return null;
  } finally {
    meshoptDecoderInitializing = false;
  }
};

// Initialize shared DRACOLoader
const getSharedDracoLoader = () => {
  if (!sharedDracoLoader) {
    sharedDracoLoader = new DRACOLoader();
    sharedDracoLoader.setDecoderPath(`${process.env.PUBLIC_URL || ''}/draco/gltf/`);
  }
  return sharedDracoLoader;
};

export function preloadDracoDecoder() {
  try {
    getSharedDracoLoader().preload();
  } catch (e) {
    console.warn('[LoadModel] Draco preload skipped', e);
  }
}

preloadDracoDecoder();

// Start initialization immediately (non-blocking)
initializeMeshoptDecoder();

const getSharedMeshoptDecoder = () => {
  // Return the shared instance if available, otherwise return null
  // The decoder will be set up asynchronously
  return sharedMeshoptDecoder;
};

// Custom hook for loading GLB files with retry logic
// Retries indefinitely until successful or component unmounts
const useGLTFWithRetry = (url, objectName, maxRetries = Infinity, timeout = 300000, onLoadFailed) => {
  const onFailedRef = useRef(onLoadFailed);
  useEffect(() => {
    onFailedRef.current = onLoadFailed;
  }, [onLoadFailed]);

  const [gltf, setGltf] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const { startLoading, stopLoading, markAsFailed, clearFailed, updateProgress } = useLoadingTracker();
  const loadWithRetryRef = useRef(null);

  useEffect(() => {
    if (!url) return;

    let cancelled = false;
    let timeoutId = null;
    let hasMarkedFailed = false;
    
    const cacheKey = `gltf_${url}`;
    
    // Check cache first
    const cachedModel = modelCache.get(cacheKey);
    if (cachedModel) {
      setGltf(cachedModel);
      setLoading(false);
      setError(null);
      if (objectName) {
        stopLoading(objectName);
      }
      return;
    }

    // Check if already loading
    const existingPromise = modelCache.getLoadingPromise(cacheKey);
    if (existingPromise) {
      existingPromise
        .then((result) => {
          if (!cancelled) {
            setGltf(result);
            setLoading(false);
            setError(null);
            if (objectName) {
              stopLoading(objectName);
            }
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err);
            setLoading(false);
            if (objectName) {
              stopLoading(objectName);
            }
          }
        });
      return () => {
        cancelled = true;
      };
    }
    
    // Start tracking loading
    if (objectName) {
      startLoading(objectName);
    }

    const loadWithRetry = async (attempt = 0) => {
      // If cancelled, stop retrying
      if (cancelled) return null;

      try {
        const loader = new GLTFLoader();
        
        // Use shared DRACOLoader to avoid creating multiple WebAssembly instances
        // Creating a new DRACOLoader for each model causes "Out of memory" errors
        // when loading many models simultaneously
        const dracoLoader = getSharedDracoLoader();
        loader.setDRACOLoader(dracoLoader);
        
        // Configure MeshoptDecoder for Meshopt-compressed GLB files
        // Use shared instance to avoid multiple WebAssembly allocations
        // Don't block on initialization - set it if available, otherwise models without Meshopt will still load
        const meshoptDecoder = getSharedMeshoptDecoder();
        if (meshoptDecoder) {
          loader.setMeshoptDecoder(meshoptDecoder);
        }
        // Initialize in background (non-blocking) for future use
        initializeMeshoptDecoder().catch(() => {
          // Silently fail - MeshoptDecoder is optional
        });
        
        const loadPromise = (async () => {
          const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
          const abortTimer = setTimeout(() => {
            try {
              controller?.abort();
            } catch {
              /* ignore */
            }
          }, timeout);

          try {
            // Cache Storage first (survives reload); network miss is written back for next visit
            const buffer = await getModelArrayBuffer(url, {
              signal: controller?.signal,
              onProgress: (info) => {
                if (objectName) updateProgress(objectName, info);
              },
            });
            if (cancelled) return null;

            if (objectName) {
              updateProgress(objectName, { phase: 'parsing', percent: 100, indeterminate: false });
            }

            // Yield so UI such as File Management can paint before a long sync parse
            await new Promise((resolve) => setTimeout(resolve, 0));
            if (cancelled) return null;

            const gltf = await new Promise((resolve, reject) => {
              try {
                loader.parse(
                  buffer,
                  '',
                  (parsed) => resolve(parsed),
                  (error) => {
                    const errorMsg = error?.message || error?.toString() || String(error) || '';
                    if (
                      errorMsg.toLowerCase().includes('wasm') ||
                      errorMsg.toLowerCase().includes('webassembly') ||
                      errorMsg.toLowerCase().includes('out of memory') ||
                      errorMsg.toLowerCase().includes('cannot allocate wasm memory')
                    ) {
                      reject(new Error(`WebAssembly memory error: ${errorMsg}`));
                    } else {
                      reject(error instanceof Error ? error : new Error(errorMsg || 'GLTF parse failed'));
                    }
                  }
                );
              } catch (e) {
                reject(e);
              }
            });
            return gltf;
          } catch (e) {
            if (e?.name === 'AbortError') {
              throw new Error(`Timeout after ${timeout}ms`);
            }
            throw e;
          } finally {
            clearTimeout(abortTimer);
          }
        })();

        const result = await loadPromise;
        if (!cancelled && result) {
          // RAM cache for this session + bytes already in Cache Storage via getModelArrayBuffer
          modelCache.set(cacheKey, result);
          modelCache.removeLoadingPromise(cacheKey);

          setGltf(result);
          setLoading(false);
          setError(null);
          if (objectName) {
            stopLoading(objectName);
            clearFailed(objectName);
          }
          console.log(`[LoadModel] Successfully loaded ${url} after ${attempt + 1} attempt(s) and cached`);
          return result;
        }
        return null;
      } catch (err) {
        if (cancelled) return;
        
        // Check if it's a retryable error - treat most errors as retryable
        // Extract error message from various possible error object structures
        const errorMessage = err?.message || err?.toString() || String(err) || '';
        const errorString = errorMessage.toLowerCase();
        
        // Also check error name and type
        const errorName = (err?.name || '').toLowerCase();
        const errorType = (err?.type || '').toLowerCase();
        
        // Check for HTTP2 protocol errors and network errors
        const isHttp2Error = 
          errorString.includes('http2') ||
          errorString.includes('err_http2_protocol_error') ||
          errorString.includes('protocol_error') ||
          errorName.includes('http2') ||
          errorType.includes('http2');
        
        const isNetworkError = 
          errorString.includes('failed to fetch') ||
          errorString.includes('networkerror') ||
          errorString.includes('network') ||
          errorString.includes('aborted') ||
          errorString.includes('timeout') ||
          errorString.includes('load failed') ||
          errorString.includes('could not load') ||
          errorString.includes('fetch') ||
          errorName.includes('network') ||
          errorName.includes('abort') ||
          errorType === 'networkerror' ||
          errorType === 'abort';
        
        const isFetchFail =
          errorString.includes('503') ||
          errorString.includes('service unavailable') ||
          errorString.includes('failed to fetch') ||
          errorString.includes('draco_wasm_wrapper') ||
          errorString.includes('draco_decoder');

        const isWebAssemblyError =
          !isFetchFail &&
          (errorString.includes('out of memory') ||
            errorString.includes('cannot allocate wasm memory') ||
            errorName === 'runtimeerror' ||
            errorName === 'rangeerror');
        
        // With maxRetries = Infinity, we should always retry for network/HTTP2 errors
        // WebAssembly errors are also retryable (might be temporary memory issues)
        // Only stop if it's a non-retryable error (like parsing errors) and we've tried many times
        const isRetryableError = isHttp2Error || isNetworkError || isWebAssemblyError;
        
        // Mark as failed immediately for WebAssembly errors, or after 5 attempts for other errors
        // WebAssembly errors should be shown immediately since they're usually memory-related
        const shouldMarkAsFailed = (isWebAssemblyError && attempt >= 1) || (attempt >= 5);
        
        if (shouldMarkAsFailed && objectName && !hasMarkedFailed) {
          hasMarkedFailed = true;
          markAsFailed(objectName, err, () => {
            hasMarkedFailed = false;
            modelCache.delete(cacheKey);
            setError(null);
            setLoading(true);
            if (objectName) {
              startLoading(objectName);
            }
            const newPromise = loadWithRetryRef.current?.(0);
            if (newPromise) {
              modelCache.setLoadingPromise(cacheKey, newPromise);
            }
          }, { backgroundRetry: true });
        }
        
        // Always retry network/HTTP2 errors indefinitely
        // For other errors, retry up to 100 times as a safety measure
        if (isRetryableError || attempt < 100) {
          // Cap exponential backoff at 30 seconds to avoid extremely long waits
          const backoffDelay = Math.min(Math.pow(2, Math.min(attempt, 5)) * 1000, 30000);
          const errorInfo = errorMessage || errorName || 'Unknown error';
          console.warn(`[LoadModel] Attempt ${attempt + 1} failed for ${url}${isRetryableError ? ' (network error)' : ''}, retrying in ${backoffDelay/1000}s...`, errorInfo);
          
          // Keep loading state true while retrying
          setLoading(true);
          setError(null); // Clear error while retrying
          
          // Exponential backoff with cap
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          
          // Continue retrying
          return loadWithRetry(attempt + 1);
        }
        
        // Only set error if we've truly exhausted retries (shouldn't happen for network errors with Infinity)
        modelCache.removeLoadingPromise(cacheKey);
        setError(err);
        setLoading(false);
        console.error(`[LoadModel] Failed to load ${url} after ${attempt + 1} attempts:`, err);
        
        if (objectName) {
          markAsFailed(objectName, err, () => {
            hasMarkedFailed = false;
            modelCache.delete(cacheKey);
            setError(null);
            setLoading(true);
            if (objectName) {
              startLoading(objectName);
            }
            const newPromise = loadWithRetryRef.current?.(0);
            if (newPromise) {
              modelCache.setLoadingPromise(cacheKey, newPromise);
            }
          });
          onFailedRef.current?.(objectName);
        }
      }
    };

    loadWithRetryRef.current = loadWithRetry;

    // Start loading and create promise
    const loadingPromise = loadWithRetry();
    modelCache.setLoadingPromise(cacheKey, loadingPromise);
    
    // Handle the promise result
    loadingPromise
      .then((result) => {
        if (result && !cancelled) {
          // Already handled in loadWithRetry, but ensure state is set
        }
      })
      .catch((err) => {
        if (!cancelled) {
          modelCache.removeLoadingPromise(cacheKey);
          // Error handling is done in loadWithRetry
        }
      });

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (objectName) {
        stopLoading(objectName);
      }
    };
  }, [url, objectName, maxRetries, timeout, startLoading, stopLoading, markAsFailed, clearFailed, updateProgress]);

  return { gltf, error, loading };
};

// Error Boundary for catching useGLTF errors
class ModelErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const { objectName, onError } = this.props;
    if (onError && objectName) {
      onError(objectName, error);
    }
    console.error(`[LoadModel] ErrorBoundary caught error for ${objectName}:`, error);
  }

  componentDidUpdate(prevProps) {
    // Reset error boundary when retryKey changes (retry triggered)
    if (prevProps.retryKey !== this.props.retryKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      // Return null to hide the broken component
      // The error is already tracked via onError callback
      return null;
    }

    return this.props.children;
  }
}

// Component that uses standard useGLTF (for smaller files or when retry is disabled)
const StandardLoadModel = ({ name, url, visible, opacity = 1, shouldBloom, groupRef, onLoadComplete }) => {
  const { startLoading, stopLoading, clearFailed } = useLoadingTracker();
  const objectName = name || url;
  const reportedRef = useRef(false);
  
  // Initialize MeshoptDecoder in background (non-blocking)
  useEffect(() => {
    initializeMeshoptDecoder().catch(() => {
      // Silently fail - MeshoptDecoder is optional
    });
  }, []);
  
  // Start tracking loading
  useEffect(() => {
    if (url && objectName) {
      startLoading(objectName);
    }
    return () => {
      if (objectName) {
        stopLoading(objectName);
      }
    };
  }, [url, objectName, startLoading, stopLoading]);
  
  const model = useGLTF(url, undefined, undefined, (loader) => {
    // Configure MeshoptDecoder for Meshopt-compressed GLB files
    // Use shared instance to avoid multiple WebAssembly allocations
    // Don't block - only set if already initialized
    if (loader && typeof loader.setMeshoptDecoder === 'function') {
      const meshoptDecoder = getSharedMeshoptDecoder();
      if (meshoptDecoder) {
        loader.setMeshoptDecoder(meshoptDecoder);
      }
    }
  });
  
  // Clear error on success
  useEffect(() => {
    if (model && objectName) {
      clearFailed(objectName);
      stopLoading(objectName);
    }
  }, [model, objectName, clearFailed, stopLoading]);

  useEffect(() => {
    if (visible && model?.scene && onLoadComplete && !reportedRef.current) {
      reportedRef.current = true;
      onLoadComplete(name);
    }
    if (!visible) reportedRef.current = false;
  }, [visible, model, name, onLoadComplete]);

  useEffect(() => {
    if (model?.scene && name) {
      tagObjectWithLayerKey(model.scene, name);
    }
  }, [model, name]);
  
  useEffect(() => {
    if (groupRef.current && shouldBloom && model) {
      groupRef.current.traverse((obj) => {
        if (obj.isMesh) {
          obj.layers.enable(1);
        }
      });
    }
  }, [shouldBloom, model, groupRef]);

  useEffect(() => {
    if (model?.scene) {
      applyLayerAppearance(model.scene, { visible, opacity });
    }
  }, [model, visible, opacity]);

  return model ? <primitive object={model.scene} ref={groupRef} visible={visible} /> : null;
};

// Component that uses retry mechanism (for large GLB files)
const RetryLoadModel = ({ name, url, visible, opacity = 1, shouldBloom, groupRef, onLoadComplete, onLoadFailed }) => {
  const { gltf, error } = useGLTFWithRetry(url, name, Infinity, 300000, onLoadFailed);
  const reportedRef = useRef(false);

  useEffect(() => {
    if (groupRef.current && shouldBloom && gltf) {
      groupRef.current.traverse((obj) => {
        if (obj.isMesh) {
          obj.layers.enable(1);
        }
      });
    }
  }, [shouldBloom, gltf, groupRef]);

  useEffect(() => {
    if (visible && gltf?.scene && onLoadComplete && !reportedRef.current) {
      reportedRef.current = true;
      onLoadComplete(name);
    }
    if (!visible) reportedRef.current = false;
  }, [visible, gltf, name, onLoadComplete]);

  useEffect(() => {
    if (gltf?.scene && name) {
      tagObjectWithLayerKey(gltf.scene, name);
    }
  }, [gltf, name]);

  useEffect(() => {
    if (gltf?.scene) {
      applyLayerAppearance(gltf.scene, { visible, opacity });
    }
  }, [gltf, visible, opacity]);

  if (error) {
    console.error(`[LoadModel] Error loading ${name || url}:`, error);
    return null;
  }

  return gltf ? <primitive object={gltf.scene} ref={groupRef} visible={visible} /> : null;
};

const LoadModel = ({ name, url, visible, opacity = 1, shouldBloom = false, useRetry = null, onLoadComplete, onLoadFailed }) => {
  const groupRef = useRef();
  const { markAsFailed, startLoading } = useLoadingTracker();
  const [retryKey, setRetryKey] = useState(0);
  const objectName = name || url;
  
  // Default to using retry mechanism for GLB files to handle network errors
  // Retry logic will keep trying until the model loads successfully
  // Can be explicitly disabled by passing useRetry={false}
  const shouldUseRetry = useRetry !== null 
    ? useRetry 
    : (url && (url.includes('.glb') || url.includes('.GLB')));

  const handleError = useCallback((objName, error) => {
    markAsFailed(objName, error, () => {
      const cacheKey = `gltf_${url}`;
      modelCache.delete(cacheKey);
      setRetryKey(prev => prev + 1);
      if (objectName) {
        startLoading(objectName);
      }
    }, { backgroundRetry: false });
    onLoadFailed?.(objName);
  }, [objectName, url, markAsFailed, startLoading, onLoadFailed]);

  return (
    <ModelErrorBoundary objectName={objectName} onError={handleError} retryKey={retryKey}>
      <Suspense fallback={null}>
        {shouldUseRetry ? (
          <RetryLoadModel 
            name={name} 
            url={url} 
            visible={visible}
            opacity={opacity}
            shouldBloom={shouldBloom} 
            groupRef={groupRef} 
            onLoadComplete={onLoadComplete}
            onLoadFailed={onLoadFailed}
          />
        ) : (
          <StandardLoadModel 
            key={retryKey}
            name={name} 
            url={url} 
            visible={visible}
            opacity={opacity}
            shouldBloom={shouldBloom} 
            groupRef={groupRef} 
            onLoadComplete={onLoadComplete}
          />
        )}
      </Suspense>
    </ModelErrorBoundary>
  );
}

export default LoadModel
